import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Truck,
  CheckCircle,
  Shirt,
  ClipboardList,
  Package,
  LayoutGrid,
  List,
  ChevronRight,
  ArrowLeft,
  Settings,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { fetchLaundryById } from '@/pages/parametrizacion/laundries/Services/laundries.services';
import EntregasEnCamino from './components/EntregasEnCamino';
import EntregasCompletadas from './components/EntregasCompletadas';
import RecoleccionRopaSucia from './components/RecoleccionRopaSucia';
import RecoleccionesCompletadas from './components/RecoleccionesCompletadas';
import InventarioPendienteRuta from './components/InventarioPendienteRuta';

const STORAGE_KEY = 'conductor-ruta-view';

const TABS = [
  {
    id: 'recoleccion',
    label: 'Recoger',
    sublabel: 'Ropa sucia',
    description: 'Registra la ropa sucia recogida de los clientes',
    icon: Shirt,
    counterKey: 'recoleccion',
    color: 'text-purple-600',
    activeBorder: 'border-b-purple-500',
    activeBg: 'bg-purple-50',
    cardGradient: 'from-purple-500 to-purple-700',
    step: 1,
  },
  {
    id: 'recolectadas',
    label: 'Recogido',
    sublabel: 'Confirmado',
    description: 'Ropa sucia ya registrada y confirmada',
    icon: ClipboardList,
    counterKey: 'recolectadas',
    color: 'text-orange-600',
    activeBorder: 'border-b-orange-500',
    activeBg: 'bg-orange-50',
    cardGradient: 'from-orange-500 to-orange-700',
    step: 2,
  },
  {
    id: 'en-camino',
    label: 'En Camino',
    sublabel: 'Por entregar',
    description: 'Confirma entregas de ropa limpia al cliente',
    icon: Truck,
    counterKey: 'enCamino',
    color: 'text-yellow-600',
    activeBorder: 'border-b-yellow-500',
    activeBg: 'bg-yellow-50',
    cardGradient: 'from-yellow-500 to-amber-600',
    step: 3,
  },
  {
    id: 'completadas',
    label: 'Entregado',
    sublabel: 'Al cliente',
    description: 'Historial de entregas confirmadas',
    icon: CheckCircle,
    counterKey: 'completadas',
    color: 'text-green-600',
    activeBorder: 'border-b-green-500',
    activeBg: 'bg-green-50',
    cardGradient: 'from-green-500 to-green-700',
    step: 4,
  },
  {
    id: 'inventario',
    label: 'Inventario',
    sublabel: 'Pendiente',
    description: 'Prendas pendientes por cliente',
    icon: Package,
    counterKey: 'inventario',
    color: 'text-blue-600',
    activeBorder: 'border-b-blue-500',
    activeBg: 'bg-blue-50',
    cardGradient: 'from-blue-500 to-blue-700',
    step: 5,
  },
];

/* ─────────────────────────────────────────
   Drawer de ajustes
───────────────────────────────────────── */
const SettingsDrawer = ({ open, onClose, viewMode, onViewModeChange }) => (
  <AnimatePresence>
    {open && (
      <>
        {/* Overlay */}
        <motion.div
          className="fixed inset-0 bg-black/40 z-40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        {/* Panel */}
        <motion.div
          className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-xl"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        >
          {/* Handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-gray-200" />
          </div>

          {/* Cabecera */}
          <div className="flex items-center justify-between px-5 py-3 border-b">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-gray-500" />
              <h2 className="text-base font-bold text-gray-800">Ajustes</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 active:scale-95 transition-all"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          {/* Contenido */}
          <div className="px-5 py-5 space-y-6">

            {/* Sección: Visualización */}
            <div className="space-y-3">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                Visualización
              </p>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Vista de rutas</p>
                <p className="text-xs text-gray-400 mb-3">
                  Elige cómo ver las secciones de tu ruta diaria.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  {/* Opción: Tarjetas */}
                  <button
                    onClick={() => onViewModeChange('cards')}
                    className={`flex flex-col items-center gap-2.5 p-4 rounded-xl border-2 transition-all active:scale-[0.97]
                      ${viewMode === 'cards'
                        ? 'border-blue-500 bg-blue-50 shadow-sm'
                        : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                      }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center
                      ${viewMode === 'cards' ? 'bg-blue-500' : 'bg-gray-200'}`}>
                      <LayoutGrid className={`h-5 w-5 ${viewMode === 'cards' ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <div className="text-center">
                      <p className={`text-sm font-bold ${viewMode === 'cards' ? 'text-blue-700' : 'text-gray-600'}`}>
                        Tarjetas
                      </p>
                      <p className="text-[10px] text-gray-400 leading-tight mt-0.5">
                        Vista general con contadores
                      </p>
                    </div>
                    {viewMode === 'cards' && (
                      <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                        <CheckCircle className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </button>

                  {/* Opción: Tabs */}
                  <button
                    onClick={() => onViewModeChange('tabs')}
                    className={`flex flex-col items-center gap-2.5 p-4 rounded-xl border-2 transition-all active:scale-[0.97]
                      ${viewMode === 'tabs'
                        ? 'border-blue-500 bg-blue-50 shadow-sm'
                        : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                      }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center
                      ${viewMode === 'tabs' ? 'bg-blue-500' : 'bg-gray-200'}`}>
                      <List className={`h-5 w-5 ${viewMode === 'tabs' ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <div className="text-center">
                      <p className={`text-sm font-bold ${viewMode === 'tabs' ? 'text-blue-700' : 'text-gray-600'}`}>
                        Tabs
                      </p>
                      <p className="text-[10px] text-gray-400 leading-tight mt-0.5">
                        Navegación rápida por pasos
                      </p>
                    </div>
                    {viewMode === 'tabs' && (
                      <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                        <CheckCircle className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Espacio para el home indicator en iOS */}
          <div className="h-6" />
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

/* ─────────────────────────────────────────
   Vista de tarjetas
───────────────────────────────────────── */
const CardView = ({ tabs, counters, onSelect }) => {
  const totalItems = tabs.reduce((s, t) => s + (counters[t.counterKey] ?? 0), 0);

  return (
    <div className="p-3 sm:p-4 space-y-3">
      {totalItems > 0 && (
        <div className="bg-white rounded-xl border px-4 py-3 flex items-center gap-3">
          <div className="flex-1">
            <p className="text-xs text-gray-500">Total pendiente hoy</p>
            <p className="text-2xl font-bold text-gray-800">{totalItems}</p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {tabs.filter((t) => (counters[t.counterKey] ?? 0) > 0).map((t) => (
              <span key={t.id} className={`text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 ${t.color}`}>
                {counters[t.counterKey]}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {tabs.map((tab, index) => {
          const Icon = tab.icon;
          const count = counters[tab.counterKey] ?? 0;
          const isAlone = tabs.length % 2 !== 0 && index === tabs.length - 1;

          return (
            <motion.button
              key={tab.id}
              onClick={() => onSelect(tab.id)}
              className={`rounded-2xl overflow-hidden shadow-sm border border-gray-100
                active:scale-[0.97] transition-transform text-left
                ${isAlone ? 'col-span-2' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06 }}
            >
              <div className={`bg-gradient-to-br ${tab.cardGradient} p-4`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white/70 text-[10px] font-semibold tracking-wide">
                    {tab.step}/{tabs.length}
                  </span>
                </div>
                <div>
                  <p className={`font-bold leading-none text-white ${isAlone ? 'text-4xl' : 'text-3xl'}`}>
                    {count > 0 ? count : '—'}
                  </p>
                  <p className="text-sm font-semibold text-white mt-1">{tab.label}</p>
                  <p className="text-xs text-white/70">{tab.sublabel}</p>
                </div>
              </div>
              <div className="bg-white px-3 py-2.5 flex items-center gap-2">
                <p className="text-xs text-gray-500 leading-tight flex-1">{tab.description}</p>
                <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   Componente principal
───────────────────────────────────────── */
const RutaClientesPage = () => {
  const { idLavanderia } = useParams();

  const [lavanderiaNombre, setLavanderiaNombre] = useState('');

  useEffect(() => {
    if (idLavanderia) {
      fetchLaundryById(idLavanderia)
        .then((data) => setLavanderiaNombre(data?.nombre || ''))
        .catch(() => setLavanderiaNombre(''));
    }
  }, [idLavanderia]);

  // Preferencia guardada: 'cards' | 'tabs'
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'cards'; }
    catch { return 'cards'; }
  });

  // Pantalla actual: 'cards' | 'detail' | 'tabs'
  //   'cards'  → grilla de tarjetas (sin tab bar)
  //   'detail' → contenido de una sección (sin tab bar, solo con preferencia cards)
  //   'tabs'   → contenido con tab bar (preferencia tabs)
  const [activeView, setActiveView] = useState(() => {
    try {
      const pref = localStorage.getItem(STORAGE_KEY) || 'cards';
      return pref === 'tabs' ? 'tabs' : 'cards';
    } catch { return 'cards'; }
  });

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('en-camino');

  const [counters, setCounters] = useState({
    alistadas: 0,
    enCamino: 0,
    completadas: 0,
    recoleccion: 0,
    recolectadas: 0,
    inventario: 0,
  });

  const [refreshKey, setRefreshKey] = useState(0);

  const tabsScrollRef = useRef(null);
  const tabRefs = useRef({});

  useEffect(() => {
    if (activeView !== 'tabs') return;
    const el = tabRefs.current[activeTab];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeTab, activeView]);

  const handleRefresh = useCallback(() => setRefreshKey((p) => p + 1), []);

  // Navega a una sección respetando la preferencia de vista
  const goToSection = useCallback((tabId) => {
    setActiveTab(tabId);
    setActiveView(viewMode === 'tabs' ? 'tabs' : 'detail');
  }, [viewMode]);

  const handleNavigateToRecolectadas = useCallback(() => {
    setRefreshKey((p) => p + 1);
    goToSection('recolectadas');
  }, [goToSection]);

  const handleNavigateToCompletadas = useCallback(() => {
    setRefreshKey((p) => p + 1);
    goToSection('completadas');
  }, [goToSection]);

  const handleCountChange = useCallback((tab, count) => {
    setCounters((prev) => ({ ...prev, [tab]: count }));
  }, []);

  const handleSelectCard = useCallback((tabId) => {
    goToSection(tabId);
  }, [goToSection]);

  // Guardar preferencia y sincronizar pantalla
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch {}
    // Al cambiar preferencia, ir al "inicio" del nuevo modo
    setActiveView(mode === 'tabs' ? 'tabs' : 'cards');
  }, []);

  const activeTabData = TABS.find((t) => t.id === activeTab);
  const showTabBar = activeView === 'tabs';
  const showContent = activeView === 'detail' || activeView === 'tabs';

  return (
    <div className="min-h-screen bg-gray-50 pb-20 sm:pb-6">

      {/* ── Header fijo ── */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">

        {/* Barra superior */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b">
          {/* Botón atrás: solo en 'detail' (preferencia cards) */}
          {activeView === 'detail' && (
            <button
              onClick={() => setActiveView('cards')}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 active:scale-95 transition-all shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}

          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">
              {lavanderiaNombre || 'Mis Rutas'}
            </p>
            {showContent && activeTabData && (
              <p className={`text-xs font-medium ${activeTabData.color}`}>
                {activeTabData.label} — {activeTabData.sublabel}
              </p>
            )}
          </div>

          {/* Botón ajustes */}
          <button
            onClick={() => setSettingsOpen(true)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 active:scale-95 transition-all shrink-0"
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>

        {/* Tab bar — solo cuando activeView === 'tabs' */}
        <AnimatePresence>
          {showTabBar && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="relative">
                <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-gray-100 to-transparent z-10" />
                <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-100 to-transparent z-10" />
                <div
                  ref={tabsScrollRef}
                  className="flex overflow-x-auto hide-scrollbar bg-gray-100 px-1 py-1 gap-1"
                >
                  {TABS.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    const count = counters[tab.counterKey] ?? 0;

                    return (
                      <button
                        key={tab.id}
                        ref={(el) => { tabRefs.current[tab.id] = el; }}
                        onClick={() => setActiveTab(tab.id)}
                        className={`
                          flex-shrink-0 flex flex-col items-center justify-center gap-0.5
                          px-3 py-2.5 min-w-[76px] rounded-lg transition-all duration-150 border-b-2
                          ${isActive
                            ? `bg-white shadow-md ${tab.activeBorder} border border-gray-200`
                            : 'border-b-transparent hover:bg-white/60 active:bg-white/80'
                          }
                        `}
                      >
                        <div className="relative">
                          <Icon className={`h-5 w-5 transition-colors ${isActive ? tab.color : 'text-gray-400'}`} />
                          {count > 0 && (
                            <Badge
                              className="absolute -top-2 -right-2.5 h-5 min-w-[20px] px-1 flex items-center justify-center text-[10px] font-bold"
                              variant={isActive ? 'default' : 'secondary'}
                            >
                              {count > 99 ? '99+' : count}
                            </Badge>
                          )}
                        </div>
                        <span className={`text-[11px] font-semibold leading-tight transition-colors ${isActive ? tab.color : 'text-gray-400'}`}>
                          {tab.label}
                        </span>
                        <span className={`text-[9px] leading-tight transition-colors ${isActive ? 'text-gray-500' : 'text-gray-300'}`}>
                          {tab.sublabel}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Contenido ── */}
      <AnimatePresence mode="wait">
        {activeView === 'cards' ? (
          <motion.div
            key="cards"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
          >
            <CardView tabs={TABS} counters={counters} onSelect={handleSelectCard} />
          </motion.div>
        ) : (
          <motion.div
            key={`content-${activeTab}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="p-3 sm:p-4"
          >
            {activeTab === 'en-camino' && (
              <EntregasEnCamino
                key={`encamino-${refreshKey}`}
                idLavanderia={idLavanderia}
                onCountChange={(count) => handleCountChange('enCamino', count)}
                onRefresh={handleRefresh}
                onNavigateToCompletadas={handleNavigateToCompletadas}
              />
            )}
            {activeTab === 'completadas' && (
              <EntregasCompletadas
                key={`completadas-${refreshKey}`}
                idLavanderia={idLavanderia}
                onCountChange={(count) => handleCountChange('completadas', count)}
              />
            )}
            {activeTab === 'recoleccion' && (
              <RecoleccionRopaSucia
                key={`recoleccion-${refreshKey}`}
                idLavanderia={idLavanderia}
                lavanderiaNombre={lavanderiaNombre}
                onCountChange={(count) => handleCountChange('recoleccion', count)}
                onRefresh={handleRefresh}
                onNavigateToRecolectadas={handleNavigateToRecolectadas}
              />
            )}
            {activeTab === 'recolectadas' && (
              <RecoleccionesCompletadas
                key={`recolectadas-${refreshKey}`}
                idLavanderia={idLavanderia}
                onCountChange={(count) => handleCountChange('recolectadas', count)}
              />
            )}
            {activeTab === 'inventario' && (
              <InventarioPendienteRuta
                key={`inventario-${refreshKey}`}
                idLavanderia={idLavanderia}
                onCountChange={(count) => handleCountChange('inventario', count)}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Drawer de ajustes ── */}
      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        viewMode={viewMode}
        onViewModeChange={(mode) => {
          handleViewModeChange(mode);
          setSettingsOpen(false);
        }}
      />
    </div>
  );
};

export default RutaClientesPage;
